import { useState } from "react";
import { X, Loader2, Upload, CheckCircle, AlertCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STAGES = ["Pre-seed", "Seed", "Series A", "Series B+"];
const CATEGORIES = ["AI Agents", "DevTools", "Infrastructure", "Vertical AI", "Other"];
const REFERRAL_SOURCES = ["Twitter/X", "Referral", "Search", "Portfolio Company", "Other"];

const PitchForm = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    // Founder info
    full_name: "",
    email: "",
    phone: "",
    linkedin_url: "",
    twitter_handle: "",
    // Company info
    company_name: "",
    website_url: "",
    tagline: "",
    description: "",
    stage: "",
    category: "",
    founded_date: "",
    team_size: "",
    // Fundraising
    amount_raising: "",
    previous_funding: "",
    current_revenue: "",
    // Materials
    pitch_deck: null,
    demo_video_url: "",
    // Additional
    why_wyco: "",
    referral_source: "",
    referral_name: "",
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        setError("Please upload a PDF file");
        return;
      }
      if (file.size > 25 * 1024 * 1024) {
        setError("File must be under 25MB");
        return;
      }
      setFormData(prev => ({ ...prev, pitch_deck: file }));
      setError(null);
    }
  };

  const validateStep = (stepNum) => {
    switch (stepNum) {
      case 1:
        return formData.full_name && formData.email && formData.linkedin_url;
      case 2:
        return formData.company_name && formData.website_url && formData.tagline && 
               formData.description && formData.stage && formData.category;
      case 3:
        return formData.amount_raising && formData.pitch_deck;
      case 4:
        return formData.why_wyco && formData.referral_source;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    } else {
      setError("Please fill in all required fields");
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) {
      setError("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const formDataToSend = new FormData();
      
      // Founder info
      formDataToSend.append("full_name", formData.full_name);
      formDataToSend.append("email", formData.email);
      if (formData.phone) formDataToSend.append("phone", formData.phone);
      formDataToSend.append("linkedin_url", formData.linkedin_url);
      if (formData.twitter_handle) formDataToSend.append("twitter_handle", formData.twitter_handle);
      
      // Company info
      formDataToSend.append("company_name", formData.company_name);
      formDataToSend.append("website_url", formData.website_url);
      formDataToSend.append("tagline", formData.tagline);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("stage", formData.stage);
      formDataToSend.append("category", formData.category);
      if (formData.founded_date) formDataToSend.append("founded_date", formData.founded_date);
      if (formData.team_size) formDataToSend.append("team_size", formData.team_size);
      
      // Fundraising
      formDataToSend.append("amount_raising", formData.amount_raising);
      if (formData.previous_funding) formDataToSend.append("previous_funding", formData.previous_funding);
      if (formData.current_revenue) formDataToSend.append("current_revenue", formData.current_revenue);
      
      // Materials
      formDataToSend.append("pitch_deck", formData.pitch_deck);
      if (formData.demo_video_url) formDataToSend.append("demo_video_url", formData.demo_video_url);
      
      // Additional
      formDataToSend.append("why_wyco", formData.why_wyco);
      formDataToSend.append("referral_source", formData.referral_source);
      if (formData.referral_name) formDataToSend.append("referral_name", formData.referral_name);

      const response = await fetch(`${BACKEND_URL}/api/pitches`, {
        method: "POST",
        body: formDataToSend,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to submit pitch");
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: "", email: "", phone: "", linkedin_url: "", twitter_handle: "",
      company_name: "", website_url: "", tagline: "", description: "", stage: "",
      category: "", founded_date: "", team_size: "", amount_raising: "",
      previous_funding: "", current_revenue: "", pitch_deck: null, demo_video_url: "",
      why_wyco: "", referral_source: "", referral_name: "",
    });
    setStep(1);
    setSubmitted(false);
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (submitted) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="bg-[#0a0a0f] border border-white/10 text-white max-w-md">
          <div className="py-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h3 className="text-xl font-mono text-[#F3F4F6] mb-2">Pitch Submitted!</h3>
            <p className="text-[#6B7280] text-sm">
              Thank you for your interest in WyCo Capital. We&apos;ll review your pitch and get back to you soon.
            </p>
            <Button
              onClick={handleClose}
              className="mt-6 bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 hover:bg-[#00F0FF]/20"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-[#0a0a0f] border border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="border-b border-white/10 pb-4">
          <DialogTitle className="font-mono text-xl text-[#F3F4F6] flex items-center justify-between">
            <span>Submit Your Pitch</span>
            <div className="flex items-center gap-2 text-sm text-[#6B7280]">
              <span>Step {step} of 4</span>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Progress Bar */}
        <div className="flex gap-1 mb-6">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={`h-1 flex-1 rounded-full transition-colors ${
                s <= step ? "bg-[#00F0FF]" : "bg-white/10"
              }`}
            />
          ))}
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg mb-4">
            <AlertCircle className="w-4 h-4 text-red-400" />
            <span className="text-red-400 text-sm">{error}</span>
          </div>
        )}

        {/* Step 1: Founder Info */}
        {step === 1 && (
          <div className="space-y-4">
            <h3 className="font-mono text-sm text-[#00F0FF] tracking-wider">FOUNDER INFORMATION</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Full Name *</label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => updateField("full_name", e.target.value)}
                  placeholder="John Doe"
                  className="bg-white/5 border-white/10 text-white placeholder:text-[#6B7280]/50"
                />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Email *</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateField("email", e.target.value)}
                  placeholder="john@startup.com"
                  className="bg-white/5 border-white/10 text-white placeholder:text-[#6B7280]/50"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Phone (Optional)</label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateField("phone", e.target.value)}
                  placeholder="+1 555 123 4567"
                  className="bg-white/5 border-white/10 text-white placeholder:text-[#6B7280]/50"
                />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">LinkedIn URL *</label>
                <Input
                  value={formData.linkedin_url}
                  onChange={(e) => updateField("linkedin_url", e.target.value)}
                  placeholder="linkedin.com/in/johndoe"
                  className="bg-white/5 border-white/10 text-white placeholder:text-[#6B7280]/50"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-xs text-[#6B7280] mb-1">X/Twitter Handle (Optional)</label>
              <Input
                value={formData.twitter_handle}
                onChange={(e) => updateField("twitter_handle", e.target.value)}
                placeholder="@johndoe"
                className="bg-white/5 border-white/10 text-white placeholder:text-[#6B7280]/50"
              />
            </div>
          </div>
        )}

        {/* Step 2: Company Info */}
        {step === 2 && (
          <div className="space-y-4">
            <h3 className="font-mono text-sm text-[#00F0FF] tracking-wider">COMPANY INFORMATION</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Company Name *</label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => updateField("company_name", e.target.value)}
                  placeholder="Acme AI"
                  className="bg-white/5 border-white/10 text-white placeholder:text-[#6B7280]/50"
                />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Website *</label>
                <Input
                  value={formData.website_url}
                  onChange={(e) => updateField("website_url", e.target.value)}
                  placeholder="https://acme.ai"
                  className="bg-white/5 border-white/10 text-white placeholder:text-[#6B7280]/50"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-xs text-[#6B7280] mb-1">One-liner / Tagline * (max 100 chars)</label>
              <Input
                value={formData.tagline}
                onChange={(e) => updateField("tagline", e.target.value.slice(0, 100))}
                placeholder="AI agents for enterprise sales"
                className="bg-white/5 border-white/10 text-white placeholder:text-[#6B7280]/50"
              />
              <span className="text-[10px] text-[#6B7280]">{formData.tagline.length}/100</span>
            </div>
            
            <div>
              <label className="block text-xs text-[#6B7280] mb-1">Description *</label>
              <Textarea
                value={formData.description}
                onChange={(e) => updateField("description", e.target.value)}
                placeholder="Tell us about your product, traction, and vision..."
                rows={4}
                className="bg-white/5 border-white/10 text-white placeholder:text-[#6B7280]/50 resize-none"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Stage *</label>
                <Select value={formData.stage} onValueChange={(v) => updateField("stage", v)}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Select stage" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0f] border-white/10">
                    {STAGES.map((s) => (
                      <SelectItem key={s} value={s} className="text-white hover:bg-white/10">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Category *</label>
                <Select value={formData.category} onValueChange={(v) => updateField("category", v)}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0f] border-white/10">
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c} className="text-white hover:bg-white/10">{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Founded (Optional)</label>
                <Input
                  type="date"
                  value={formData.founded_date}
                  onChange={(e) => updateField("founded_date", e.target.value)}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Team Size (Optional)</label>
                <Input
                  type="number"
                  value={formData.team_size}
                  onChange={(e) => updateField("team_size", e.target.value)}
                  placeholder="5"
                  className="bg-white/5 border-white/10 text-white placeholder:text-[#6B7280]/50"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Fundraising & Materials */}
        {step === 3 && (
          <div className="space-y-4">
            <h3 className="font-mono text-sm text-[#00F0FF] tracking-wider">FUNDRAISING & MATERIALS</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Amount Raising *</label>
                <Input
                  value={formData.amount_raising}
                  onChange={(e) => updateField("amount_raising", e.target.value)}
                  placeholder="$2M"
                  className="bg-white/5 border-white/10 text-white placeholder:text-[#6B7280]/50"
                />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Previous Funding (Optional)</label>
                <Input
                  value={formData.previous_funding}
                  onChange={(e) => updateField("previous_funding", e.target.value)}
                  placeholder="$500k pre-seed"
                  className="bg-white/5 border-white/10 text-white placeholder:text-[#6B7280]/50"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-xs text-[#6B7280] mb-1">Current Revenue / MRR (Optional)</label>
              <Input
                value={formData.current_revenue}
                onChange={(e) => updateField("current_revenue", e.target.value)}
                placeholder="$50k MRR"
                className="bg-white/5 border-white/10 text-white placeholder:text-[#6B7280]/50"
              />
            </div>
            
            <div>
              <label className="block text-xs text-[#6B7280] mb-1">Pitch Deck * (PDF, max 25MB)</label>
              <div className="relative">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  id="pitch-deck-upload"
                />
                <label
                  htmlFor="pitch-deck-upload"
                  className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                    formData.pitch_deck
                      ? "border-green-500/50 bg-green-500/5"
                      : "border-white/10 hover:border-[#00F0FF]/30 hover:bg-[#00F0FF]/5"
                  }`}
                >
                  {formData.pitch_deck ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span className="text-green-400 text-sm">{formData.pitch_deck.name}</span>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          updateField("pitch_deck", null);
                        }}
                        className="ml-2 p-1 hover:bg-white/10 rounded"
                      >
                        <X className="w-4 h-4 text-[#6B7280]" />
                      </button>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 text-[#6B7280]" />
                      <span className="text-[#6B7280] text-sm">Click to upload pitch deck</span>
                    </>
                  )}
                </label>
              </div>
            </div>
            
            <div>
              <label className="block text-xs text-[#6B7280] mb-1">Demo Video URL (Optional)</label>
              <Input
                value={formData.demo_video_url}
                onChange={(e) => updateField("demo_video_url", e.target.value)}
                placeholder="https://loom.com/share/..."
                className="bg-white/5 border-white/10 text-white placeholder:text-[#6B7280]/50"
              />
            </div>
          </div>
        )}

        {/* Step 4: Why WyCo */}
        {step === 4 && (
          <div className="space-y-4">
            <h3 className="font-mono text-sm text-[#00F0FF] tracking-wider">FINAL QUESTIONS</h3>
            
            <div>
              <label className="block text-xs text-[#6B7280] mb-1">Why WyCo? *</label>
              <Textarea
                value={formData.why_wyco}
                onChange={(e) => updateField("why_wyco", e.target.value)}
                placeholder="Why do you think WyCo Capital is the right partner for your company?"
                rows={4}
                className="bg-white/5 border-white/10 text-white placeholder:text-[#6B7280]/50 resize-none"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">How did you hear about us? *</label>
                <Select value={formData.referral_source} onValueChange={(v) => updateField("referral_source", v)}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0f] border-white/10">
                    {REFERRAL_SOURCES.map((s) => (
                      <SelectItem key={s} value={s} className="text-white hover:bg-white/10">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(formData.referral_source === "Referral" || formData.referral_source === "Portfolio Company") && (
                <div>
                  <label className="block text-xs text-[#6B7280] mb-1">Referrer Name</label>
                  <Input
                    value={formData.referral_name}
                    onChange={(e) => updateField("referral_name", e.target.value)}
                    placeholder="Who referred you?"
                    className="bg-white/5 border-white/10 text-white placeholder:text-[#6B7280]/50"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4 border-t border-white/10 mt-6">
          <Button
            variant="outline"
            onClick={() => step > 1 ? setStep(step - 1) : handleClose()}
            className="border-white/10 text-[#6B7280] hover:text-white hover:border-white/20"
          >
            {step === 1 ? "Cancel" : "Back"}
          </Button>
          
          {step < 4 ? (
            <Button
              onClick={handleNext}
              className="bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 hover:bg-[#00F0FF]/20"
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#00F0FF] text-[#0a0a0f] hover:bg-[#00F0FF]/90"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit Pitch"
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PitchForm;
